import React, { useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Button, Chip, Divider, Menu, MenuItem, Popover, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import { NAMED_EMPTY } from './settingsKit';
import { type LensDocument, type LensDocumentData, isTabs } from './types';
import type { BoardEditor } from './useBoardEdit';

type Kind = 'calendar' | 'kpis' | 'line' | 'area' | 'column' | 'combo' | 'bar' | 'pie' | 'table' | 'heatmap' | 'scatter' | 'bridge' | 'box' | 'bullet' | 'text' | 'callout';

const KINDS: Array<[Kind, string]> = [
  ['kpis', 'Figures'],
  ['bullet', 'Against a target'],
  ['line', 'Trend'],
  ['area', 'Area'],
  ['column', 'Columns'],
  ['combo', 'Two measures over time'],
  ['bar', 'Breakdown'],
  ['pie', 'Share'],
  ['table', 'Table'],
  ['heatmap', 'Heatmap'],
  ['calendar', 'Calendar'],
  ['scatter', 'Scatter'],
  ['bridge', 'Bridge'],
  ['box', 'Box plot'],
  ['text', 'Text'],
  ['callout', 'Callout'],
];

const GRAINS: Array<[string, string]> = [
  ['hour', 'Hourly'],
  ['day', 'Daily'],
  ['week', 'Weekly'],
  ['month', 'Monthly'],
  ['quarter', 'Quarterly'],
];

const TIMED: Array<Kind> = ['line', 'area', 'column', 'combo'];
const SPLIT: Record<Kind, 'required' | 'optional' | undefined> = { calendar: undefined, kpis: undefined, bullet: 'optional', line: 'optional', area: 'optional', column: 'optional', combo: undefined, bar: 'required', pie: 'required', table: 'optional', heatmap: 'required', scatter: 'required', bridge: 'required', box: 'optional', text: undefined, callout: undefined };
const SECOND: Array<Kind> = ['combo', 'scatter'];

type FormProps = { data?: LensDocumentData; editor: BoardEditor; after?: string; onAdded?: () => void };

function AddBlockForm({ data, editor, after, onAdded }: FormProps) {
  const fields = useGetReportingLensFields(data?.lensId, data?.lensVersion);
  const [kind, setKind] = useState<Kind>('kpis');
  const [measure, setMeasure] = useState('');
  const [second, setSecond] = useState('');
  const [split, setSplit] = useState('');
  const [thenBy, setThenBy] = useState('');
  const [grain, setGrain] = useState('week');
  const [text, setText] = useState('');
  const [column, setColumn] = useState('');
  const [target, setTarget] = useState('');
  const datasets = fields.data?.datasets ?? [];
  const certified = datasets.flatMap((dataset) => dataset.certified.map((entry) => ({ ...entry, dataset: dataset.id })));
  const shared = (id: string) => certified.filter((entry) => entry.id === id).length > 1;
  const local = new Set((fields.data?.measures ?? []).map((entry) => entry.id));
  const measures = [
    ...(fields.data?.measures ?? []).map((entry) => ({ ref: entry.id, label: entry.label })),
    ...certified.filter((entry) => !local.has(entry.id)).map((entry) => ({ ref: shared(entry.id) ? `${entry.dataset}.${entry.id}` : entry.id, label: entry.label })),
  ];
  const splits = [...new Map(datasets.flatMap((dataset) => dataset.columns.filter((entry) => entry.kind === 'dimension').map((entry) => [entry.name, entry.label] as const))).entries()];
  const numeric = datasets.flatMap((dataset) => dataset.columns.filter((entry) => entry.kind === 'measure').map((entry): [string, string] => [`${dataset.id}.${entry.name}`, `${entry.label} · ${dataset.label}`]));
  const prose = kind === 'text' || kind === 'callout';
  const targetValue = target.trim() === '' ? undefined : Number.isFinite(Number(target)) ? Number(target) : target.trim();
  const ready = prose
    ? Boolean(text.trim())
    : (kind === 'box' ? Boolean(column) : Boolean(measure)) && (SPLIT[kind] !== 'required' || Boolean(split)) && (!SECOND.includes(kind) || Boolean(second)) && (kind !== 'heatmap' || Boolean(thenBy)) && (kind !== 'bullet' || targetValue !== undefined);
  const by = split ? [split, ...(kind === 'heatmap' && thenBy ? [thenBy] : [])] : undefined;
  const block = (): Record<string, unknown> => {
    if (prose) return { [kind]: text.trim() };
    if (kind === 'kpis') return { kpis: [measure] };
    if (kind === 'bullet') return { kpi: measure, form: 'bullet', target: targetValue, ...(by ? { by } : {}) };
    if (kind === 'calendar') return { calendar: measure };
    if (kind === 'combo') return { combo: [measure, second], grain };
    if (kind === 'box') return { box: column, ...(by ? { by } : {}) };
    if (kind === 'scatter') return { scatter: [measure, second], by };
    if (kind === 'table') return { table: [measure], ...(by ? { by } : {}) };
    if (TIMED.includes(kind)) return { [kind]: measure, grain, ...(by ? { by } : {}) };
    return { [kind]: measure, by };
  };
  const add = () => {
    editor.apply({ op: 'add', block: block(), ...(after ? { after } : {}) });
    setText('');
    onAdded?.();
  };
  const choose = (label: string, value: string, onChange: (next: string) => void, options: Array<[string, string]>, empty?: string) => (
    <TextField fullWidth {...(empty !== undefined ? NAMED_EMPTY : {})} label={label} onChange={(event) => onChange(event.target.value)} select size="small" value={value}>
      {empty !== undefined ? <MenuItem value="">{empty}</MenuItem> : null}
      {options.map(([id, optionLabel]) => (
        <MenuItem key={id} value={id}>
          {optionLabel}
        </MenuItem>
      ))}
    </TextField>
  );
  const measureOptions = measures.map((entry): [string, string] => [entry.ref, entry.label]);

  return (
    <Stack data-lens-add-block spacing={1.5}>
      <Stack aria-label="Kind of block" direction="row" flexWrap="wrap" gap={0.5} role="radiogroup">
        {KINDS.map(([value, label]) => (
          <Chip aria-checked={kind === value} clickable color={kind === value ? 'primary' : 'default'} key={value} label={label} onClick={() => setKind(value)} role="radio" size="small" variant={kind === value ? 'filled' : 'outlined'} />
        ))}
      </Stack>
      {prose ? (
        <TextField fullWidth label={kind === 'callout' ? 'Callout' : 'Text'} minRows={2} multiline onChange={(event) => setText(event.target.value)} placeholder="Write {measure} for a live number" size="small" value={text} />
      ) : kind === 'box' ? (
        choose('Spread of', column, setColumn, numeric)
      ) : (
        <TextField disabled={fields.isLoading} fullWidth helperText={fields.error ? 'The measures couldn’t be read.' : undefined} label={SECOND.includes(kind) ? 'First measure' : 'Measure'} onChange={(event) => setMeasure(event.target.value)} select size="small" value={measure}>
          {measureOptions.map(([id, label]) => (
            <MenuItem key={id} value={id}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {SECOND.includes(kind) ? choose('Second measure', second, setSecond, measureOptions.filter(([id]) => id !== measure)) : null}
      {kind === 'bullet' ? <TextField fullWidth helperText="A number, or a measure id such as budget_ttv." label="Target" onChange={(event) => setTarget(event.target.value)} size="small" value={target} /> : null}
      {SPLIT[kind] ? choose('Dimension 1', split, setSplit, splits, SPLIT[kind] === 'optional' ? (kind === 'table' ? 'One total row' : 'No split') : undefined) : null}
      {kind === 'heatmap' ? choose('Across', thenBy, setThenBy, splits.filter(([name]) => name !== split)) : null}
      {TIMED.includes(kind) ? (
        <ToggleButtonGroup aria-label="Grain" exclusive fullWidth onChange={(_, next: string | null) => next && setGrain(next)} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }} value={grain}>
          {GRAINS.map(([value, label]) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Typography color="text.secondary" variant="caption">
          {after ? 'Goes after the selected block.' : 'Goes at the end of the board.'}
        </Typography>
        <Button disabled={!ready || editor.pending} onClick={add} size="small" startIcon={<AddIcon />} variant="text">
          Add block
        </Button>
      </Stack>
    </Stack>
  );
}

type StarterKind = 'figure' | 'chart' | 'bars' | 'table' | 'note';

const STARTERS: Array<[StarterKind, string]> = [
  ['figure', 'Figure'],
  ['chart', 'Chart'],
  ['bars', 'Ranked bars'],
  ['table', 'Table'],
  ['note', 'Text note'],
];

type AddBlockProps = {
  anchor: HTMLElement | null;
  onClose: () => void;
  editor: BoardEditor;
  data?: LensDocumentData;
  document?: LensDocument;
  after?: string;
};

/** Starters take the Lens's first measure and first split; "More kinds" opens the full form for the rest. */
export default function AddBlock({ anchor, onClose, editor, data, document, after }: AddBlockProps) {
  const fields = useGetReportingLensFields(data?.lensId, data?.lensVersion);
  const [custom, setCustom] = useState(false);
  const opened = useRef(anchor);
  if (anchor) opened.current = anchor;
  const measure = fields.data?.measures[0]?.id ?? fields.data?.datasets.flatMap((dataset) => dataset.certified)[0]?.id;
  const split = fields.data?.datasets.flatMap((dataset) => dataset.columns).find((column) => column.kind === 'dimension')?.name;
  const place = after ? { after } : {};
  const add = (kind: StarterKind) => {
    const by = split ? { by: [split] } : {};
    const block: Record<string, unknown> =
      kind === 'note' ? { text: 'Write a note about this Lens.' } : kind === 'figure' ? { kpis: [measure] } : kind === 'chart' ? { line: measure, grain: 'week' } : kind === 'bars' ? { bar: measure, ...by } : { table: [measure], ...by };
    editor.apply({ op: 'add', block, ...place });
    onClose();
  };
  const needsMeasure = (kind: StarterKind) => kind !== 'note' && !measure;
  const tabs = document?.page.find(isTabs);
  return (
    <>
      <Menu anchorEl={anchor} onClose={onClose} open={Boolean(anchor) && !custom}>
        {STARTERS.map(([kind, label]) => (
          <MenuItem disabled={editor.pending || needsMeasure(kind) || (kind === 'bars' && !split)} key={kind} onClick={() => add(kind)}>
            {label}
          </MenuItem>
        ))}
        <MenuItem onClick={() => setCustom(true)}>More kinds…</MenuItem>
        <Divider />
        <MenuItem
          disabled={editor.pending}
          onClick={() => {
            editor.apply({ op: 'tab', title: `Tab ${(tabs?.tabs.length ?? 1) + 1}` });
            onClose();
          }}
        >
          Add a tab
        </MenuItem>
      </Menu>
      <Popover
        anchorEl={opened.current}
        onClose={() => {
          setCustom(false);
          onClose();
        }}
        open={Boolean(anchor) && custom}
        slotProps={{ paper: { sx: { p: 2, width: 340 } } }}
      >
        <AddBlockForm after={after} data={data} editor={editor} onAdded={() => { setCustom(false); onClose(); }} />
      </Popover>
    </>
  );
}

/** The panel's own entry point when no block is selected. */
export function AddBlockButton({ editor, data, document }: Omit<AddBlockProps, 'anchor' | 'onClose' | 'after'>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button aria-haspopup="menu" disabled={editor.pending} onClick={(event) => setAnchor(event.currentTarget)} size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }} variant="text">
        Add block
      </Button>
      <AddBlock anchor={anchor} data={data} document={document} editor={editor} onClose={() => setAnchor(null)} />
    </>
  );
}
